import { useEffect, useMemo, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { ThemeProvider } from "@/context/ThemeContext";
import { AuthProvider } from "@/context/AuthContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import RoleGuard from "@/components/auth/RoleGuard";
import ComingSoonGate from "@/components/ComingSoonGate";
import type { SiteSettings } from "@/services/siteSettingsRepository";
import staticPages from "@/data/staticPages.json";
import { buildInformationMenu } from "@/lib/staticPageMenu";
import { rpc } from "@/client/rpc";
import SeoMetadata from "@/components/SeoMetadata";
import { staticRouteSeo } from "@/seo/metadata";

const fallbackSettings: SiteSettings = {
  comingSoonEnabled: false,
  comingSoonMessage: "Axiom Manifold opens soon.",
  comingSoonTargetAt: "2026-07-11T18:00:00+10:00",
  comingSoonBackgroundImage: "/images/landing-page-banner-images/banner-002.webp",
  futureConfig: {},
};

export default function RootLayout({
  children,
}: {
  children?: React.ReactNode;
}) {
  const [settings, setSettings] = useState<SiteSettings>(fallbackSettings);
  const informationMenu = buildInformationMenu(staticPages);
  const { pathname } = useLocation();
  const routeSeo = useMemo(
    () => pathname === "/product" || pathname.startsWith("/product/") ? null : staticRouteSeo(pathname),
    [pathname],
  );
  const isControlPlane = pathname === "/admin"
    || pathname.startsWith("/admin/")
    || pathname === "/mail"
    || pathname.startsWith("/mail/");

  useEffect(() => {
    let active = true;
    rpc<SiteSettings>("getSiteSettings")
      .then((nextSettings) => active && setSettings(nextSettings))
      .catch((error) => console.warn("Site settings unavailable; using open-site defaults.", error));
    return () => { active = false; };
  }, []);

  return (
    <AuthProvider>
      <ThemeProvider>
        <SeoMetadata metadata={routeSeo} />
        <ComingSoonGate settings={settings}>
          <Header controlPlane={isControlPlane} informationMenu={informationMenu} />
          <main className={`flex-grow flex flex-col bg-background text-foreground transition-colors duration-300${isControlPlane ? " control-plane-main" : ""}`}>
            <RoleGuard>{children ?? <Outlet />}</RoleGuard>
          </main>
          {!isControlPlane && <Footer />}
        </ComingSoonGate>
      </ThemeProvider>
    </AuthProvider>
  );
}
